import { Skeleton } from "@gigpro/ui/Skeleton";
import { cn } from "@gigpro/ui/tailwind/cn";
import { isOwnDomEvent } from "@gigpro/ui/utils/isOwnDomEvent";
import type { JobCandidateStageEnum } from "@sidegigllc/api/client";
import { Link, useParams } from "@tanstack/react-router";
import type { ProBadge } from "api/resources/pros";
import { ProAvatar } from "domains/pros/components/Avatar";
import { ProCompletionRate } from "domains/pros/components/CompletionRate";
import { ProGigsWorked } from "domains/pros/components/GigsWorked";
import { ProBadges } from "domains/pros/components/ProBadges";
import { RatingDisplay } from "domains/pros/components/RatingDisplay";
import { MatchBadge } from "../MatchBadge";
import { PreferredProChip } from "../PreferredProChip";

export type JobCandidateCardData = {
  id: number | string;
  first_name: string;
  last_name: string;
  user?: number | null;
  image_id?: string | null;
  stage?: JobCandidateStageEnum;
  match_percentage?: number;
  badges?: ProBadge | null;
  is_preferred_pro?: boolean;
  star_rating?: number;
  shifts_done?: number;
  completion_rate?: number;
  prospect_notes?: string | null;
};

interface JobCandidateCardProps {
  candidate: JobCandidateCardData;
  variant?: "vertical" | "horizontal";
  onClick?: () => void;
  className?: string;
  /**
   * Renders a plain div root instead of a Link/button — for cards that host their own
   * interactive children (e.g. nested buttons) or are purely display-only. Keeps the card
   * out of the tab order, unlike a `pointer-events-none` Link which stays keyboard-focusable.
   * @default false
   */
  nonInteractive?: boolean;
  /**
   * Interactive control overlaid on the top-right of the card (e.g. a favorite toggle).
   * Must stop click propagation itself since the card root may be clickable.
   */
  overlay?: React.ReactNode;
  /**
   * Rendered directly under the pro's name (e.g. a CRM status tag).
   */
  status?: React.ReactNode;
  /**
   * Card content, rendered at the bottom of the card body.
   */
  children?: React.ReactNode;
}

const baseCardClassName = [
  "relative gap-2 rounded-sm border border-light-gray bg-white",
  "data-[orientation=horizontal]:grid data-[orientation=horizontal]:min-w-72 data-[orientation=horizontal]:grid-cols-[auto_1fr_auto] data-[orientation=horizontal]:items-start data-[orientation=horizontal]:p-2",
  "data-[orientation=vertical]:flex data-[orientation=vertical]:min-w-56 data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-center data-[orientation=vertical]:p-2.5",
];

// Only interactive (clickable) cards react to hover; inert cards keep a static border.
const interactiveCardClassName = "transition-colors hover:border-brand";

export const JobCandidateCard: React.FC<JobCandidateCardProps> = ({
  candidate,
  variant = "vertical",
  onClick,
  className,
  nonInteractive = false,
  overlay,
  status,
  children,
}) => {
  const { locationId } = useParams({ strict: false });
  const isHorizontal = variant === "horizontal";

  const cardClassName = cn(baseCardClassName, className);

  const overlayContent = overlay && <div className="absolute top-2 right-2 z-10">{overlay}</div>;

  const content = isHorizontal ? (
    <HorizontalContent candidate={candidate} status={status}>
      {children}
    </HorizontalContent>
  ) : (
    <VerticalContent candidate={candidate} status={status}>
      {children}
    </VerticalContent>
  );

  if (nonInteractive) {
    return (
      <div data-orientation={variant} className={cardClassName}>
        {overlayContent}
        {content}
      </div>
    );
  }

  if (onClick) {
    // A native <button> can't wrap the interactive children some cards host (schedule/favorite actions),
    // so use a role="button" div with keyboard support to keep the card clickable without nesting buttons.
    return (
      <div
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- native element cannot carry this markup
        role="button"
        tabIndex={0}
        data-orientation={variant}
        onClick={(event) => {
          if (isOwnDomEvent(event)) onClick();
        }}
        onKeyDown={(event) => {
          // Ignore keys bubbling from descendants — including React-tree events replayed from
          // portaled children (e.g. an open dialog) — so space/enter only activates the card itself.
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
          }
        }}
        className={cn(cardClassName, interactiveCardClassName, "cursor-pointer text-left")}
      >
        {overlayContent}
        {content}
      </div>
    );
  }

  if (locationId != null && candidate.user != null) {
    return (
      <Link
        to="/locations/$locationId/connect/pros/$proId"
        params={{ locationId, proId: String(candidate.user) }}
        data-orientation={variant}
        className={cn(cardClassName, interactiveCardClassName)}
      >
        {overlayContent}
        {content}
      </Link>
    );
  }

  return (
    <div data-orientation={variant} className={cn(cardClassName, interactiveCardClassName)}>
      {overlayContent}
      {content}
    </div>
  );
};

type ContentProps = {
  candidate: JobCandidateCardData;
  status?: React.ReactNode;
  children?: React.ReactNode;
};

const VerticalContent: React.FC<ContentProps> = ({ candidate, status, children }) => (
  <>
    <div className="relative">
      <ProAvatar imageId={candidate.image_id} size={80} disableZoom />
      {candidate.match_percentage != null && (
        <MatchBadge
          matchPercentage={candidate.match_percentage}
          size="small"
          className="absolute bottom-1.5 left-1/2 -translate-x-1/2 translate-y-1/2"
        />
      )}
    </div>

    <div className="flex w-full flex-col items-start gap-1">
      <div className="flex items-center gap-1">
        <span className="text-normal font-semibold tracking-tight">
          {candidate.first_name} {candidate.last_name}
        </span>
        {candidate.badges && <ProBadges badges={candidate.badges} className="h-4" />}
      </div>

      {status}

      {candidate.is_preferred_pro && <PreferredProChip />}

      {(candidate.star_rating != null || candidate.shifts_done != null) && (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2 text-normal tracking-tight">
            {candidate.star_rating != null && <RatingDisplay rating={candidate.star_rating} />}
            {candidate.shifts_done != null && <ProGigsWorked gigsWorked={candidate.shifts_done} />}
          </div>
          {candidate.completion_rate != null && <ProCompletionRate completionRate={candidate.completion_rate / 100} />}
        </div>
      )}

      {children}
    </div>
  </>
);

const HorizontalContent: React.FC<ContentProps> = ({ candidate, status, children }) => (
  <>
    <ProAvatar imageId={candidate.image_id} size={60} disableZoom />

    <div className="flex min-w-0 flex-col gap-1">
      {candidate.is_preferred_pro && <PreferredProChip />}

      <div className="flex items-center gap-1">
        <span className="truncate text-large font-semibold">
          {candidate.first_name} {candidate.last_name}
        </span>
        {candidate.badges && <ProBadges badges={candidate.badges} className="h-4 shrink-0" />}
      </div>

      {status}

      {(candidate.star_rating != null || candidate.shifts_done != null) && (
        <div className="flex items-center gap-1.5 tracking-tight">
          {candidate.star_rating != null && <RatingDisplay rating={candidate.star_rating} />}
          {candidate.shifts_done != null && <ProGigsWorked gigsWorked={candidate.shifts_done} />}
        </div>
      )}

      {candidate.completion_rate != null && <ProCompletionRate completionRate={candidate.completion_rate / 100} />}

      {children}
    </div>

    {candidate.match_percentage != null && <MatchBadge matchPercentage={candidate.match_percentage} />}
  </>
);

export const JobCandidateCardSkeleton: React.FC<{ className?: string; variant?: "vertical" | "horizontal" }> = ({
  className,
  variant = "vertical",
}) => {
  if (variant === "horizontal") {
    return (
      <div data-orientation={variant} className={cn(baseCardClassName, className)}>
        <Skeleton variant="circle" className="size-12" />
        <div className="flex flex-col gap-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-40" />
        </div>
      </div>
    );
  }

  return (
    <div data-orientation={variant} className={cn(baseCardClassName, className)}>
      <Skeleton variant="circle" className="size-16" />
      <div className="flex w-full flex-col items-start gap-1">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-28" />
      </div>
    </div>
  );
};
